import * as modelSrcs from "url:../../models/exported/*.gltf";
import manifestoSlideSrcsHigh from "url:../../maps/manifesto/high/*.ktx2";
import manifestoSlideSrcsMed from "url:../../maps/manifesto/medium/*.ktx2";
import manifestoSlideSrcsLow from "url:../../maps/manifesto/low/*.ktx2";
import content from "../../content.json";

import * as classes from "./main-canvas.module.scss";
import { useContext, useEffect, useRef } from "react";
import WorldScene from "../../scene/WorldScene";
import {
  ActionType,
  AppContext,
  ControlsMode,
  Modal,
  ModalType,
  ViewMode,
  World,
} from "../../contexts/AppContext";
import ControlsInstructions from "./ControlsInstructions";
import Easing from "easing-functions";

import DeviceGradeDetection, {
  DeviceGrade,
} from "../../util/DeviceGradeDetection";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import DissolveShader from "../../shaders/DissolveShader/DissolveShader";

import ticker from "../../util/Ticker";
import WebGLView from "../../WebGLView";
import { uiAudio } from "../../App";
import Joystick from "../Joystick/Joystick";
import classNames from "classnames";
import { Color, sRGBEncoding, Vector2 } from "three";
import ObjectStickyLabel from "../ObjectStickyLabel/ObjectStickyLabel";
import { getTextureSize, TextureSize } from "../../util/three-utils";
import ManifestoScene from "../../scene/ManifestoScene";

let composer, renderPass, bloomPass, shaderPass;

let overlayScene;

const MainCanvas = () => {
  const { state, dispatch } = useContext(AppContext);
  const {
    isInit,
    world,
    modal,
    manifestoSlide,
    isMainCanvasPaused,
    controlsMode,
    isWorldDissolving,
    isWorldLoading,
    viewMode,
  } = state || {};

  const scene = useRef<{
    name: string;
    scene: WorldScene;
    isLoading: boolean;
  } | null>(null);
  const modalRef = useRef<Modal | null>(modal);

  useEffect(() => {
    const init = async () => {
      (window as any).webGlView = new WebGLView(
        document.querySelector(".canvas-main")
      );

      (window as any).textureSize = await getTextureSize();
      await (window as any).webGlView.init();
      (window as any).webGlView.renderer.clear();

      const manifestoSlideSrcs =
        // window.innerWidth <= 390
        //   ? manifestoSlideSrcsLow
        //   :
        window.innerWidth <= 1024
          ? manifestoSlideSrcsMed
          : manifestoSlideSrcsHigh;
      overlayScene = new ManifestoScene(
        Object.keys(manifestoSlideSrcs).map((key) => manifestoSlideSrcs[key])
      );

      if (DeviceGradeDetection.deviceGrade >= DeviceGrade.High) {
        composer = new EffectComposer((window as any).webGlView.renderer);
        renderPass = new RenderPass(null, null);
        composer.addPass(renderPass);
        bloomPass = new UnrealBloomPass(
          new Vector2(
            window.innerWidth * window.devicePixelRatio,
            window.innerHeight * devicePixelRatio
          ),
          3,
          1,
          0
        );
        composer.addPass(bloomPass);
        shaderPass = new ShaderPass(DissolveShader);
        composer.addPass(shaderPass);

        composer.renderTarget1.texture.encoding = sRGBEncoding;
        composer.renderTarget2.texture.encoding = sRGBEncoding;
      }

      dispatch({ type: ActionType.OnInit });

      overlayScene?.init();
    };
    init();

    return () => {
      (window as any).webGlView.dispose();
    };
  }, []);

  useEffect(() => {
    if (isInit) {
      if (isMainCanvasPaused) {
        (window as any).webGlView.pause();
      } else {
        (window as any).webGlView.unpause();
      }
    }
  }, [isMainCanvasPaused, isInit]);

  useEffect(() => {
    const onPointerLockChange = (e) => {
      if (
        document.pointerLockElement ||
        (document as any).mozPointerLockElement
      ) {
        dispatch({ type: ActionType.SetPointerLocked, value: true });
      } else {
        dispatch({ type: ActionType.SetPointerLocked, value: false });
      }
    };
    const onPointerLockError = () => {};

    if (dispatch) {
      document.addEventListener("pointerlockchange", onPointerLockChange);
      document.addEventListener("pointerlockerror", onPointerLockError);
    }

    return () => {
      document.removeEventListener("pointerlockchange", onPointerLockChange);
      document.removeEventListener("pointerlockerror", onPointerLockError);
    };
  }, [dispatch]);

  useEffect(() => {
    const dissolveOut = () => {
      return new Promise((resolve) => {
        let composerTime = 0;
        let composerProgress = 0;

        if (shaderPass) {
          (shaderPass.uniforms.uProgress.value as any) = 0;
        }

        const updateShaderPass = (delta) => {
          composerTime += (1 / 60) * delta;
          composerProgress = Easing.Quadratic.In(composerTime * 0.5);

          if (shaderPass) {
            (shaderPass.uniforms.uProgress.value as any) = composerProgress;
          }
          if (bloomPass) {
            bloomPass.strength = composerProgress * 0.1;
            bloomPass.radius = composerProgress * 0.5;
          }

          if (composerProgress >= 1) {
            renderPass.scene = null;
            renderPass.camera = null;
            (window as any).webGlView.setEffectComposer(null);
            ticker.removeCallback(updateShaderPass);
            resolve(true);
          }
        };

        renderPass.scene = (window as any).webGlView.scene;
        renderPass.camera = (window as any).webGlView.scene.camera;
        updateShaderPass(1);
        (window as any).webGlView.setEffectComposer(composer);
        ticker.addCallback(updateShaderPass);
      });
    };

    const wait = (ms = 3333) => {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    };

    const changeScene = async (world: World | null) => {
      if (!scene.current) {
        (window as any).webGlView.renderer.clear();
      }

      if (world !== null) {
        dispatch({ type: ActionType.SetIsWorldLoading, value: true });
      }

      if (scene.current && !scene.current.isLoading) {
        uiAudio.stop(content.worlds[scene.current.name].soundtrack, true);

        if (composer) {
          dispatch({ type: ActionType.SetIsWorldDissolving, value: true });
          await dissolveOut();
          dispatch({ type: ActionType.SetIsWorldDissolving, value: false });
        }

        (window as any).webGlView.renderer.setClearColor(new Color(0x000000));
        (window as any).webGlView.renderer.setClearAlpha(0);
        await scene.current.scene.dispose();
        scene.current = null;
        (window as any).webGlView.setEffectComposer(null);
        (window as any).webGlView.updateScene(null);
        (window as any).webGlView.renderer.clear();
        (window as any).webGlView.refreshRenderer(); // safari doesn't clean up array buffers transfered from web worker > main thread, unless we recreated the webgl renderer
      } else if (world === null && scene.current && scene.current.isLoading) {
        scene.current = null;
      }

      if (world !== null) {
        const enumName = World[world];
        let newScene = new WorldScene(
          world,
          modelSrcs[content.worlds[enumName].glb],
          content.worlds[enumName].lookGlbs.map((key) => modelSrcs[key]),
          content.worlds[enumName].plantGlbs.map((key) => modelSrcs[key]),
          content.worlds[enumName].instancedMeshes,
          content.worlds[enumName].skyConfig
        );
        scene.current = {
          name: enumName,
          scene: newScene,
          isLoading: true,
        };
        // we add a wait in here to allow a minimum time to show the loading screen for (give user time to read content)
        await Promise.all([scene.current.scene.init(), wait(3333)]);
        dispatch({ type: ActionType.SetIsWorldLoading, value: false });
        if (!scene.current) {
          // user navigated away before init
          newScene.dispose();
          newScene = undefined;
          (window as any).webGlView.setEffectComposer(null);
          (window as any).webGlView.updateScene(null);
          (window as any).webGlView.renderer.clear();
          (window as any).webGlView.refreshRenderer(); // safari doesn't clean up array buffers transfered from web worker > main thread, unless we recreated the webgl renderer
        } else {
          scene.current.isLoading = false;
          (window as any).webGlView.renderer.setClearColor(
            new Color(content.worlds[enumName].skyConfig.colourFallback)
          );
          (window as any).webGlView.updateScene(scene.current.scene);
        }
      } else {
        scene.current = null;
        (window as any).webGlView.updateScene(null);
        dispatch({ type: ActionType.SetIsWorldLoading, value: false });
      }

      (window as any).webGlView.renderer.info.reset();
      console.info((window as any).webGlView.renderer.info.memory);
    };

    if (isInit) {
      changeScene(world);
    }
  }, [world, isInit]);

  useEffect(() => {
    if (modal && scene.current) {
      scene.current.scene.disableControls();
    } else if (!modal && scene.current) {
      scene.current.scene.enableControls();
    }

    if (modal && modal.type === ModalType.Manifesto) {
      (window as any).webGlView.updateOverlayScene(overlayScene);
      overlayScene.isVisible = true;
    } else if (
      modalRef.current &&
      modalRef.current.type === ModalType.Manifesto
    ) {
      overlayScene.isVisible = false;
      setTimeout(() => {
        (window as any).webGlView.updateOverlayScene(overlayScene);
      }, 2222);
    }

    modalRef.current = modal;
  }, [modal]);

  useEffect(() => {
    if (overlayScene && manifestoSlide !== null) {
      overlayScene.slideIndex = manifestoSlide;
    }
  }, [manifestoSlide]);

  return (
    <>
      <canvas
        onClick={() => {
          if (scene.current) {
            scene.current.scene.enableControls();
          }
        }}
        className={classNames("canvas-main", classes.root, {
          [classes["root--visible"]]:
            viewMode === ViewMode.World || isWorldDissolving,
        })}
        aria-hidden={viewMode !== ViewMode.World}
      ></canvas>
      <div
        className={classNames(classes["ui-wrapper"], {
          [classes["ui-wrapper--visible"]]:
            !modal &&
            !isWorldLoading &&
            !isWorldDissolving &&
            viewMode === ViewMode.World,
        })}
        aria-hidden={
          viewMode !== ViewMode.World ||
          !!modal ||
          isWorldLoading ||
          isWorldDissolving
        }
      >
        <ObjectStickyLabel />
        {!isWorldLoading ? <ControlsInstructions /> : null}
        {controlsMode === ControlsMode.Joystick ? (
          <>
            <div
              className={classNames(
                classes.joystick,
                classes["joystick--left"]
              )}
            >
              <Joystick name="left" />
            </div>
            <div
              className={classNames(
                classes.joystick,
                classes["joystick--right"]
              )}
            >
              <Joystick name="right" />
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

export default MainCanvas;
